// Die eine Datei, die auf eine Anmeldung wartet.
//
// Warum es diesen Speicher gibt: eine Datei, die über die Dateizuordnung
// hereinkommt („Öffnen mit Schnittklar", lib/fileLaunch.ts), ist ein
// `File`-Objekt *in der Seite*. Das Anmelden ist eine Navigation, und eine
// Navigation nimmt jedes Objekt der Seite mit. Ohne einen Ort, der die
// Navigation überlebt, hieße „melde dich an" für jeden Gast „und wähle deine
// Datei danach noch einmal" -- genau der Verlust, den diese Runde vermeiden
// soll (die Datei ist hier, anders als beim Teilen-Ziel, noch zu retten).
//
// **Eigene Datenbank** neben der der Entwürfe (`schnittklar`, editor/
// indexedDb.ts) und nicht ein dritter Speicher darin. Zwei Gründe:
//
//   * Was hier liegt, ist flüchtig und gehört niemandem: eine einzige Datei,
//     höchstens für einen Tag, verbraucht beim ersten Zugriff. Die Entwürfe
//     daneben sind die Arbeit eines Kontos und überleben Wochen. Ein
//     Versionssprung an der Entwurfs-Datenbank wegen dieses Beiwerks wäre
//     Risiko am falschen Ort (an ihr hängen der Editor, der Gast-Trichter und
//     die Offline-Seite).
//   * Aufräumen ist so eine Zeile: die ganze Datenbank löschen. Der
//     Service-Worker tut das beim Abmelden mit (`clearOfflineData`) -- eine
//     Datei, die jemand geöffnet und dann doch nicht angelegt hat, bleibt
//     nicht für die nächste Person an diesem Gerät liegen.
//
// Alles hier ist „best effort", wie bei den Entwürfen (editor/guestResult.ts):
// ein privater Modus, dem IndexedDB verweigert wird, macht diese Seite
// vergesslich und nicht kaputt.

export const PENDING_LAUNCH_DATABASE = 'schnittklar-launch'
export const PENDING_LAUNCH_STORE = 'pending'

// Ein fester Schlüssel: es wartet immer höchstens eine Datei. Ein zweiter
// Start überschreibt die erste, und das ist die richtige Antwort -- wer eine
// zweite Datei öffnet, meint die zweite.
export const PENDING_LAUNCH_KEY = 'file'

// Wie lange eine Datei wartet. Ein Tag deckt „ich melde mich gleich an" und
// „ich mache das morgen früh"; eine Datei von vorletzter Woche, die beim
// nächsten Anmelden von selbst ein Projekt würde (und einen Slot der
// Tagesquote kostete), wäre eine Überraschung und kein Dienst.
export const PENDING_LAUNCH_TTL_MS = 24 * 60 * 60 * 1000

type PendingRecord = { key: string; file: File; storedAt: number }

export type PendingLaunchStorage = {
  put(file: File, now?: number): Promise<void>
  /** Liest **und** löscht: eine wartende Datei wird genau einmal ein Projekt. */
  take(now?: number): Promise<File | null>
  clear(): Promise<void>
}

/**
 * Ob eine wartende Datei noch gilt.
 *
 * Eine Uhr, die zurückgestellt wurde (Zeitzone, Sommerzeit, ein manuell
 * gestelltes Gerät), lässt `now` vor `storedAt` liegen. Eine Datei „aus der
 * Zukunft" wegzuwerfen wäre die falsche Strenge -- das Alter ist dann
 * unbekannt, nicht groß.
 */
export function isFreshLaunch(storedAt: number, now: number): boolean {
  return now - storedAt < PENDING_LAUNCH_TTL_MS
}

/** Für vitest und für einen Browser ohne IndexedDB. */
export function memoryPendingLaunchStorage(): PendingLaunchStorage {
  let record: PendingRecord | null = null

  return {
    put: async (file, now = Date.now()) => {
      record = { key: PENDING_LAUNCH_KEY, file, storedAt: now }
    },
    take: async (now = Date.now()) => {
      const waiting = record
      record = null
      if (waiting === null || !isFreshLaunch(waiting.storedAt, now)) return null

      return waiting.file
    },
    clear: async () => {
      record = null
    },
  }
}

function open(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(PENDING_LAUNCH_DATABASE, 1)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(PENDING_LAUNCH_STORE)) {
        db.createObjectStore(PENDING_LAUNCH_STORE, { keyPath: 'key' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
    request.onblocked = () => reject(request.error)
  })
}

function awaitRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export function indexedDbPendingLaunchStorage(factory: IDBFactory): PendingLaunchStorage {
  const run = async <T>(mode: IDBTransactionMode, act: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await open(factory)
    const transaction = db.transaction(PENDING_LAUNCH_STORE, mode)
    return awaitRequest(act(transaction.objectStore(PENDING_LAUNCH_STORE)))
  }

  const forget = () =>
    run('readwrite', (store) => store.delete(PENDING_LAUNCH_KEY))
      .then(() => undefined)
      .catch(() => undefined)

  return {
    put: (file, now = Date.now()) =>
      run('readwrite', (store) => store.put({ key: PENDING_LAUNCH_KEY, file, storedAt: now } satisfies PendingRecord))
        .then(() => undefined)
        .catch(() => undefined),

    // Gelöscht wird in jedem Fall, auch wenn die Datei zu alt war: liegen
    // bleiben darf sie nicht, und ein zweiter Versuch fände dasselbe vor.
    take: async (now = Date.now()) => {
      const record = await run(
        'readonly',
        (store) => store.get(PENDING_LAUNCH_KEY) as IDBRequest<PendingRecord | undefined>,
      ).catch(() => undefined)
      await forget()

      if (!record || !(record.file instanceof File)) return null

      return isFreshLaunch(record.storedAt, now) ? record.file : null
    },

    clear: forget,
  }
}

/** Der Speicher dieser Seite, oder null in einem Browser ohne IndexedDB. */
export function defaultPendingLaunchStorage(): PendingLaunchStorage | null {
  if (typeof indexedDB === 'undefined') return null

  return indexedDbPendingLaunchStorage(indexedDB)
}
